import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, MessageCircle, LogOut, Settings } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatDateHeure, initiales } from "@/lib/format";
import { Logo } from "./Logo";
import { ApercuBandeau } from "@/components/theme/ApercuBandeau";

export const Notifications = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/notifications").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const nonLues = items.filter((n) => !n.lu).length;
  const ouvrir = (o) => { if (o && nonLues) api.post("/notifications/lues").then(() => setItems((l) => l.map((n) => ({ ...n, lu: true })))); };
  return (
    <Popover onOpenChange={ouvrir}>
      <PopoverTrigger asChild>
        <button type="button" aria-label="Notifications" data-testid="topbar-notifications" className="at-icon-btn">
          <Bell size={20} />
          {nonLues > 0 && <span data-testid="notifications-badge" className="absolute top-2 right-2 w-2.5 h-2.5 at-pill" style={{ background: "var(--temp-1)" }} />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="at-popover w-[min(340px,calc(100vw-24px))] p-4">
        <p className="font-bold mb-3">Notifications</p>
        {items.length === 0 && <p className="at-muted text-sm">Aucune notification.</p>}
        <div className="space-y-2" data-testid="notifications-list">
          {items.map((n) => (
            <Link key={n.id} to={n.lien || "/"} className="at-soft p-3 block min-h-[44px]" data-testid={`notification-${n.id}`}>
              <p className="font-semibold text-sm">{n.titre}</p>
              <p className="text-sm at-muted mt-0.5">{n.texte}</p>
              <p className="text-xs at-muted mt-1">{formatDateHeure(n.date)}</p>
            </Link>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Messages = () => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" aria-label="Messages" data-testid="topbar-messages" className="at-icon-btn"><MessageCircle size={20} /></button>
    </PopoverTrigger>
    <PopoverContent align="end" className="at-popover w-[min(320px,calc(100vw-24px))] p-4">
      <p className="font-bold mb-1">Messages</p>
      <p className="at-muted text-sm">Aucun message. La messagerie (WhatsApp, SMS, e-mail) arrive dans un prochain lot.</p>
    </PopoverContent>
  </Popover>
);

export const Avatar = () => {
  const { user, deconnexion } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="topbar-avatar" aria-label="Mon compte" className="min-w-[44px] min-h-[44px] flex items-center justify-center">
          <span className="w-10 h-10 at-pill flex items-center justify-center font-bold text-sm" style={{ background: "var(--accent)", color: "var(--accent-ink)" }}>{initiales(user)}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="at-popover w-60 p-2">
        <div className="px-2 py-2">
          <p className="font-bold" data-testid="avatar-user-name">{user.prenom} {user.nom}</p>
          <p className="text-sm at-muted" data-testid="avatar-user-role">{user.role_label}{user.partenaire_nom ? ` · ${user.partenaire_nom}` : ""}</p>
        </div>
        <DropdownMenuItem data-testid="avatar-menu-parametres" className="min-h-[44px] rounded-xl cursor-pointer" onClick={() => navigate("/parametres/compte")}>
          <Settings size={16} className="mr-2" />Paramètres
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="avatar-menu-deconnexion" className="min-h-[44px] rounded-xl cursor-pointer" onClick={async () => { await deconnexion(); navigate("/connexion"); }}>
          <LogOut size={16} className="mr-2" />Se déconnecter
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const TopBar = () => (
  <div className="sticky top-0 z-30">
    <ApercuBandeau />
    <header data-testid="topbar" className="flex items-center justify-between h-[72px] px-4 md:px-8 backdrop-blur-xl" style={{ background: "color-mix(in srgb, var(--bg) 70%, transparent)" }}>
      <Logo />
      <div className="flex items-center gap-1"><Messages /><Notifications /><Avatar /></div>
    </header>
  </div>
);
